import {
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  RadioGroup,
  Show,
  Switch,
  Typography,
} from "components";
import { WebsiteDetails } from "Pages/MyWebiste/types";
import { Controller, useFormContext } from "react-hook-form";
import { useAppSelector } from "store/store";
import { AGREEMENT_SIGNATURE_SOURCE, KYC_OPTIONS } from "utils/constants";
import { PAYMENT_METHOD } from "utils/helpers";
import { validateNotWhitespaceOnly } from "utils/validationSchema";

const WebsiteConfigurations = () => {
  const form = useFormContext<WebsiteDetails>();

  const customAgreementTemplates = useAppSelector(
    (state) => state.myWebsite.customAgreementTemplates,
  );

  const prefixWatch = form.watch([
    "websiteConfiguration.codePrefix",
    "websiteConfiguration.codeLength",
  ]);

  return (
    <Grid2 container spacing={4} p={2}>
      <Grid2 size={12}>
        <Typography fontSize={14} fontWeight={700} color="var(--neutral-900)">
          Website Configuration
        </Typography>
      </Grid2>

      <Grid2 container size={12} spacing={2}>
        <Grid2 size={12}>
          <Typography
            fontSize={10}
            fontWeight={600}
            color={"var(--neutral-500)"}
            textTransform={"uppercase"}
            letterSpacing={1}
          >
            Onboarding
          </Typography>
        </Grid2>

        <Grid2 size={12}>
          <Controller
            name="websiteConfiguration.customizeSetting.kyc"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ gap: 9 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label={"Include KYC in onboarding flow"}
                labelPlacement="start"
              />
            )}
          />
        </Grid2>
        <Show.When
          isTrue={form.watch("websiteConfiguration.customizeSetting.kyc")}
        >
          <Grid2 size={4}>
            <Controller
              name="websiteConfiguration.kycType"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select KYC type",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  disableClearable
                  requiredField
                  inputLabel="KYC Type"
                  options={KYC_OPTIONS}
                  onChange={(event, val) => field.onChange(val?.value || null)}
                  value={
                    KYC_OPTIONS.find((type) => type.value === field.value)
                      ?.name || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Show.When>

        <Grid2 size={12}>
          <Controller
            name="websiteConfiguration.customizeSetting.riskprofiling"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ gap: 2.3 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label={"Include risk profiling in onboarding flow"}
                labelPlacement="start"
              />
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <Controller
            name="websiteConfiguration.customizeSetting.agreement"
            control={form.control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ gap: 3.3 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label={"Include agreement in onboarding flow"}
                labelPlacement="start"
              />
            )}
          />
        </Grid2>
        <Show.When
          isTrue={form.watch("websiteConfiguration.customizeSetting.agreement")}
        >
          <Grid2 size={4}>
            <Controller
              name="websiteConfiguration.agreementTemplate"
              control={form.control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKSearchSelect
                    {...field}
                    inputLabel="Agreement Template"
                    disableClearable={false}
                    options={customAgreementTemplates}
                    value={
                      customAgreementTemplates?.find(
                        (dataItem) => dataItem.value === field.value,
                      )?.name || ""
                    }
                    onChange={(event, val) =>
                      field.onChange(val ? val?.value : "")
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="websiteConfiguration.agreement"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select agreement",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  disableClearable
                  requiredField
                  inputLabel="Agreement Sign Type"
                  options={AGREEMENT_SIGNATURE_SOURCE}
                  onChange={(event, val) => field.onChange(val?.value || null)}
                  value={
                    AGREEMENT_SIGNATURE_SOURCE.find(
                      (type) => type.value === field.value,
                    )?.name || []
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Show.When>
      </Grid2>

      <Grid2 size={12} my={1}>
        <Divider />
      </Grid2>

      <Grid2 size={12}>
        <Typography
          fontSize={10}
          fontWeight={600}
          color={"var(--neutral-500)"}
          textTransform={"uppercase"}
          letterSpacing={1}
        >
          Portfolio code & invoice series
        </Typography>
      </Grid2>

      <Grid2 size={12} container spacing={3}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={600} color={"var(--grey-900)"}>
            Code Length
          </Typography>
          <Controller
            name={"websiteConfiguration.codeLength"}
            control={form.control}
            render={({ field }) => (
              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <RadioGroup
                  {...field}
                  onChange={(e) => field.onChange(+e.target.value)}
                  value={+field.value}
                >
                  <Grid2
                    container
                    size={12}
                    display={"flex"}
                    alignItems={"center"}
                  >
                    <Grid2>
                      <FormControlLabel
                        value={3}
                        control={<GKRadio disableLeftPadding />}
                        label={<Typography>3-digits</Typography>}
                        sx={{ mr: "8px" }}
                      />
                    </Grid2>
                    <Grid2>
                      <FormControlLabel
                        value={4}
                        control={<GKRadio disableLeftPadding />}
                        label={<Typography>4-digits</Typography>}
                        sx={{ mr: "8px" }}
                      />
                    </Grid2>
                    <Grid2>
                      <FormControlLabel
                        value={5}
                        control={<GKRadio disableLeftPadding />}
                        label={<Typography>5-digits</Typography>}
                      />
                    </Grid2>
                  </Grid2>
                </RadioGroup>
              </FormControl>
            )}
          />
        </Grid2>
        <Grid2 container size={12} spacing={1}>
          <Grid2 size={4}>
            <Controller
              name="websiteConfiguration.codePrefix"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter prefix",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Prefix"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          {prefixWatch && (
            <Grid2
              size={12}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-start"}
              gap={1}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color={"var(--neutral-500)"}
              >
                Portfolio code will be
              </Typography>
              <Typography fontWeight={500}>{`${form.getValues(
                "websiteConfiguration.codePrefix",
              )}${"0".repeat(form.getValues("websiteConfiguration.codeLength"))}`}</Typography>
            </Grid2>
          )}
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name="websiteConfiguration.invoiceSeries"
            control={form.control}
            render={({ field }) => {
              return <GKTextField {...field} inputLabel={"Invoice Series"} />;
            }}
          />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="websiteConfiguration.revenueWithGst"
            control={form.control}
            render={({ field }) => {
              return (
                <FormControl>
                  <FormLabel
                    sx={{
                      mb: 1.5,
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--grey-900)",
                    }}
                  >
                    Revenue with GST
                  </FormLabel>
                  <RadioGroup
                    {...field}
                    value={String(field.value)}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<GKRadio disableLeftPadding />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<GKRadio disableLeftPadding />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Grid2>
      </Grid2>

      <Grid2 size={12} my={1}>
        <Divider />
      </Grid2>

      <Grid2 size={12}>
        <Typography
          fontSize={10}
          fontWeight={600}
          color={"var(--neutral-500)"}
          textTransform={"uppercase"}
          letterSpacing={1}
        >
          Payment Method
        </Typography>
      </Grid2>

      <Grid2 size={4}>
        <Controller
          name="websiteConfiguration.paymentMethods"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please select payment collection method",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <GKSearchSelect
              {...field}
              requiredField
              inputLabel="Payment Collection Method"
              disableClearable={false}
              options={PAYMENT_METHOD}
              value={
                PAYMENT_METHOD?.find(
                  (dataItem) => dataItem.value === field.value,
                )?.name || ""
              }
              onChange={(event, val) => {
                field.onChange(val?.value);
              }}
              getOptionLabel={(option) => option.name || option}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid2>
    </Grid2>
  );
};

export default WebsiteConfigurations;
